<template>
  <form @submit.prevent="save" ref="tileEditForm">
    <div class="modal" tabindex="-1" role="dialog" backdrop="static" id="editDashboardTileModal" @keyup.esc="onEsc($event)">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header header">
            <h3 class="modal-title">{{ $t(isNew ? 'editDashboardTileModal.titleAdd' : 'editDashboardTileModal.titleEdit') }}</h3>
            <button type="button" class="btn-close btn-close-white" @click="close" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="form">
              <div v-show="errorMessage" class="alert alert-danger failed">{{ errorMessage }}</div>

              <div class="form-group">
                <label>{{ $t('editDashboardTileModal.kind') }}</label>
                <div class="btn-group d-flex" role="group">
                  <button type="button" :class="form.kind === 'period' ? 'btn btn-primary' : 'btn btn-outline-secondary'" @click="setKind('period')">
                    {{ $t('editDashboardTileModal.kindPeriod') }}
                  </button>
                  <button type="button" :class="form.kind === 'momentary' ? 'btn btn-primary' : 'btn btn-outline-secondary'" @click="setKind('momentary')">
                    {{ $t('editDashboardTileModal.kindMomentary') }}
                  </button>
                  <button type="button" :class="form.kind === 'status' ? 'btn btn-primary' : 'btn btn-outline-secondary'" @click="setKind('status')">
                    {{ $t('editDashboardTileModal.kindStatus') }}
                  </button>
                </div>
              </div>

              <!-- status tiles reference a scope instead of a sensor -->
              <div class="form-group" v-if="form.kind === 'status'">
                <label for="tileStatusScope">{{ $t('editDashboardTileModal.statusScope') }}</label>
                <select id="tileStatusScope" class="form-control" v-model="form.statusScope">
                  <option value="organization">{{ $t('editDashboardTileModal.statusScopeOrganization') }}</option>
                  <option value="facility">{{ $t('editDashboardTileModal.statusScopeFacility') }}</option>
                </select>
              </div>

              <div class="form-group" v-if="form.kind !== 'status' || form.statusScope === 'facility'">
                <label for="tileFacility">{{ $t('editDashboardTileModal.facility') }}</label>
                <select id="tileFacility" class="form-control" v-model="form.facilityId" @change="form.deviceId = null; resetSource()">
                  <option v-for="facility in facilities" :key="facility.id" :value="facility.id">{{ facility.name }}</option>
                </select>
              </div>

              <div class="form-group" v-if="form.kind !== 'status' && devices.length > 1">
                <label for="tileDevice">{{ $t('editDashboardTileModal.device') }}</label>
                <select id="tileDevice" class="form-control" v-model="form.deviceId" @change="resetSource()">
                  <option v-for="device in devices" :key="device.id" :value="device.id">{{ device.name || device.apikey }}</option>
                </select>
              </div>

              <div class="form-group" v-if="form.kind !== 'status'">
                <label for="tileSource">{{ $t('editDashboardTileModal.source') }}</label>
                <select id="tileSource" class="form-control" v-model="form.sourceKey" @change="onSourceChange()">
                  <optgroup v-if="sensors.length" :label="$t('editDashboardTileModal.sensors')">
                    <option v-for="sensor in sensors" :key="'s' + sensor.id" :value="'s:' + sensor.id">{{ sensor.name }}</option>
                  </optgroup>
                  <optgroup v-if="form.kind === 'period' && collections.length" :label="$t('editDashboardTileModal.collections')">
                    <option v-for="collection in collections" :key="'c' + collection.hexaddress" :value="'c:' + collection.hexaddress">{{ collection.name }}</option>
                  </optgroup>
                </select>
              </div>

              <div class="form-group" v-if="form.kind !== 'status' && valueOptions.length">
                <label for="tileValue">{{ $t('editDashboardTileModal.value') }}</label>
                <select id="tileValue" class="form-control" v-model="form.valueKey">
                  <option v-for="option in valueOptions" :key="option.systempart + '/' + option.valuetype" :value="option.systempart + '/' + option.valuetype">
                    {{ valueLabel(option) }}
                  </option>
                </select>
              </div>
              <div class="alert alert-warning" v-else-if="form.kind !== 'status' && form.sourceKey">
                {{ $t('editDashboardTileModal.noValues') }}
              </div>

              <div class="form-group" v-if="form.kind === 'period'">
                <label for="tilePeriod">{{ $t('editDashboardTileModal.periodLabel') }}</label>
                <select id="tilePeriod" class="form-control" v-model="form.period">
                  <option value="day">{{ $t('dashboardPanel.period.day') }}</option>
                  <option value="week">{{ $t('dashboardPanel.period.week') }}</option>
                  <option value="month">{{ $t('dashboardPanel.period.month') }}</option>
                  <option value="year">{{ $t('dashboardPanel.period.year') }}</option>
                </select>
              </div>

              <div class="form-group">
                <label for="tileTitle">{{ $t('editDashboardTileModal.tileTitle') }}</label>
                <input type="text" id="tileTitle" class="form-control" v-model.trim="form.title" :placeholder="suggestedTitle" maxlength="60" />
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="submit" class="btn btn-primary" :disabled="!canSave">{{ $t(isNew ? 'editDashboardTileModal.buttonAdd' : 'editDashboardTileModal.buttonSave') }}</button>
            <button type="button" class="btn btn-secondary" @click="close">{{ $t('editDashboardTileModal.close') }}</button>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import { Modal } from 'bootstrap'
import { mapGetters } from 'vuex'
import ModalViewMixin from '@/mixins/ModalViewMixin.js'
import { newTileId, periodValueOptionsForType, momentaryValueOptions } from '@/utils/dashboard'

//
// Add/edit one dashboard tile. Pure draft editing — the panel owns the
// config and persists it; this modal only emits the finished tile.
//
export default {
  name: 'EditDashboardTileModal',
  mixins: [ModalViewMixin],
  props: {
    editTile: {
      type: Object,
      default: null
    }
  },
  emits: ['modalClosed', 'saveTile'],
  data () {
    return {
      errorMessage: '',
      form: {
        kind: 'period',
        facilityId: null,
        deviceId: null,
        sourceKey: null, // 's:<sensorId>' | 'c:<hexaddress>'
        valueKey: null, // '<systempart>/<valuetype>'
        period: 'week',
        statusScope: 'organization', // status tiles: 'organization' | 'facility'
        title: ''
      }
    }
  },
  computed: {
    ...mapGetters(['organizations']),
    isNew () {
      return !this.editTile
    },
    facilities () {
      const organization = this.organizations && this.organizations.length > 0 ? this.organizations[0] : null
      return organization ? organization.facilities || [] : []
    },
    devices () {
      const facility = this.facilities.find(f => f.id === this.form.facilityId)
      return facility ? (facility.devices || []).filter(d => d.enabled) : []
    },
    currentDevice () {
      if (this.devices.length === 0) return null
      return this.devices.find(d => d.id === this.form.deviceId) || this.devices[0]
    },
    sensors () {
      return this.currentDevice ? (this.currentDevice.sensors || []).filter(s => s.enabled) : []
    },
    collections () {
      return this.currentDevice ? (this.currentDevice.collections || []).filter(c => c.name && c.name.length > 0) : []
    },
    selectedSensor () {
      if (!this.form.sourceKey || !this.form.sourceKey.startsWith('s:')) return null
      const id = Number(this.form.sourceKey.slice(2))
      return this.sensors.find(s => s.id === id) || null
    },
    selectedCollection () {
      if (!this.form.sourceKey || !this.form.sourceKey.startsWith('c:')) return null
      const hexaddress = this.form.sourceKey.slice(2)
      return this.collections.find(c => c.hexaddress === hexaddress) || null
    },
    valueOptions () {
      if (this.form.kind === 'momentary') {
        return momentaryValueOptions(this.selectedSensor)
      }
      const type = this.selectedSensor
        ? this.selectedSensor.type
        : (this.selectedCollection ? this.selectedCollection.sensortype : null)
      return type != null ? periodValueOptionsForType(type) : []
    },
    suggestedTitle () {
      if (this.form.kind === 'status') {
        if (this.form.statusScope === 'facility') {
          const facility = this.facilities.find(f => f.id === this.form.facilityId)
          return this.$t('dashboardPanel.statusTitlePrefix') + (facility ? ' — ' + facility.name : '')
        }
        return this.$t('dashboardPanel.statusTitlePrefix') + ' — ' + this.$t('dashboardPanel.statusScopeOrganization')
      }
      const source = this.selectedSensor || this.selectedCollection
      if (!source) return ''
      if (this.form.kind === 'momentary') return source.name
      return source.name + ' — ' + this.$t('dashboardPanel.period.' + this.form.period)
    },
    canSave () {
      if (this.form.kind === 'status') {
        return this.form.statusScope === 'organization' ||
          (this.form.statusScope === 'facility' && this.form.facilityId != null)
      }
      return this.form.sourceKey && this.form.valueKey && (this.form.kind !== 'period' || this.form.period)
    }
  },
  watch: {
    modalOpened () {
      this.errorMessage = ''
      if (this.editTile) {
        this.initFromTile(this.editTile)
      } else {
        this.initFresh()
      }
    },
    valueOptions (options) {
      // keep the value selection valid when source/kind changes
      if (!options.some(o => o.systempart + '/' + o.valuetype === this.form.valueKey)) {
        this.form.valueKey = options.length > 0 ? options[0].systempart + '/' + options[0].valuetype : null
      }
    }
  },
  methods: {
    initFresh () {
      this.form.kind = 'period'
      this.form.facilityId = this.facilities.length > 0 ? this.facilities[0].id : null
      this.form.deviceId = null
      this.form.period = 'week'
      this.form.statusScope = 'organization'
      this.form.title = ''
      this.resetSource()
    },
    initFromTile (tile) {
      this.form.kind = tile.kind
      this.form.period = tile.period || 'week'
      this.form.title = tile.title || ''
      if (tile.kind === 'status') {
        this.form.statusScope = tile.statusScope || 'organization'
        this.form.facilityId = tile.facilityId != null
          ? tile.facilityId
          : (this.facilities.length > 0 ? this.facilities[0].id : null)
        return
      }
      this.form.statusScope = 'organization'
      this.preselectSourceOf(tile)
      this.form.valueKey = tile.systempart + '/' + tile.valuetype
    },
    // locate the tile's source to pre-select facility/device/source
    preselectSourceOf (tile) {
      for (const facility of this.facilities) {
        for (const device of facility.devices || []) {
          if (tile.sensorId != null && (device.sensors || []).some(s => s.id === tile.sensorId)) {
            this.form.facilityId = facility.id
            this.form.deviceId = device.id
            this.form.sourceKey = 's:' + tile.sensorId
            return
          }
          if (tile.collectionAddress && (device.collections || []).some(c => c.hexaddress === tile.collectionAddress)) {
            this.form.facilityId = facility.id
            this.form.deviceId = device.id
            this.form.sourceKey = 'c:' + tile.collectionAddress
            return
          }
        }
      }
    },
    setKind (kind) {
      this.form.kind = kind
      if (kind === 'momentary' && this.selectedCollection) {
        this.resetSource() // momentary tiles are sensor-only
      }
      if (kind !== 'status' && !this.form.sourceKey) {
        // coming back from a status draft: restore a usable source cascade
        if (this.form.facilityId == null && this.facilities.length > 0) {
          this.form.facilityId = this.facilities[0].id
        }
        this.resetSource()
      }
    },
    resetSource () {
      const firstSensor = this.sensors.length > 0 ? this.sensors[0] : null
      this.form.sourceKey = firstSensor ? 's:' + firstSensor.id : null
      this.onSourceChange()
    },
    onSourceChange () {
      const options = this.valueOptions
      this.form.valueKey = options.length > 0 ? options[0].systempart + '/' + options[0].valuetype : null
    },
    valueLabel (option) {
      return this.attributeLabelForCode('systemPart', option.systempart) + ' — ' +
        this.attributeLabelForCode('valueType', option.valuetype)
    },
    close () {
      this.errorMessage = ''
      this.emitClosed()
      Modal.getInstance(document.getElementById('editDashboardTileModal'))?.hide()
    },
    save () {
      if (!this.canSave) return
      if (this.form.kind === 'status') {
        const tile = {
          id: this.editTile ? this.editTile.id : newTileId(),
          title: this.form.title || this.suggestedTitle,
          kind: 'status',
          statusScope: this.form.statusScope
        }
        if (this.form.statusScope === 'facility') {
          tile.facilityId = this.form.facilityId
        }
        this.$emit('saveTile', tile)
        this.flagChanges()
        return
      }
      const [systempart, valuetype] = this.form.valueKey.split('/').map(Number)
      const tile = {
        id: this.editTile ? this.editTile.id : newTileId(),
        title: this.form.title || this.suggestedTitle,
        kind: this.form.kind,
        systempart,
        valuetype
      }
      if (this.selectedSensor) {
        tile.sensorId = this.selectedSensor.id
      } else if (this.selectedCollection) {
        tile.collectionAddress = this.selectedCollection.hexaddress
      } else {
        this.errorMessage = this.$t('editDashboardTileModal.noValues')
        return
      }
      if (this.form.kind === 'period') {
        tile.period = this.form.period
      }
      this.$emit('saveTile', tile)
      this.flagChanges()
    }
  }
}
</script>

<style lang="scss" scoped>
.header {
  background-color: #6695c2;
  color: white;
}

.form-group {
  margin-bottom: 12px;
}
</style>
